import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as DocumentIcon,
  Folder as FolderIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertPhoto as PhotoIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';

// Mock data for documents
const mockFolders = [
  { id: 1, name: 'Invoices', count: 12 },
  { id: 2, name: 'Receipts', count: 18 },
  { id: 3, name: 'Contracts', count: 5 },
  { id: 4, name: 'Tax Documents', count: 8 },
];

const mockDocuments = [
  { 
    id: 1, 
    name: 'Invoice #1001.pdf', 
    type: 'pdf', 
    size: '156 KB', 
    date: '2023-03-20', 
    folder: 'Invoices',
    icon: <PdfIcon color="error" />
  },
  { 
    id: 2, 
    name: 'Receipt-March.jpg', 
    type: 'image', 
    size: '2.3 MB', 
    date: '2023-03-15', 
    folder: 'Receipts',
    icon: <ImageIcon color="primary" />
  },
  { 
    id: 3, 
    name: 'Service Agreement.pdf', 
    type: 'pdf', 
    size: '420 KB', 
    date: '2023-03-10', 
    folder: 'Contracts',
    icon: <PdfIcon color="error" />
  },
  { 
    id: 4, 
    name: 'VAT Return Q1.pdf', 
    type: 'pdf', 
    size: '180 KB', 
    date: '2023-03-05', 
    folder: 'Tax Documents',
    icon: <PdfIcon color="error" />
  },
  { 
    id: 5, 
    name: 'Store Photo.png', 
    type: 'image', 
    size: '3.8 MB', 
    date: '2023-02-28', 
    folder: 'Other',
    icon: <PhotoIcon color="success" />
  },
  { 
    id: 6, 
    name: 'Supplier List.docx', 
    type: 'document', 
    size: '76 KB', 
    date: '2023-02-20', 
    folder: 'Other',
    icon: <ArticleIcon color="info" />
  },
];

function Documents() {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter documents based on search query
  const filteredDocuments = mockDocuments.filter(doc => 
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.folder.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Documents
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<FolderIcon />}
          >
            New Folder
          </Button>
          <Button 
            variant="contained" 
            startIcon={<UploadIcon />}
          >
            Upload
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Folders
            </Typography>
            <List dense>
              <ListItem button selected>
                <ListItemIcon>
                  <DocumentIcon />
                </ListItemIcon>
                <ListItemText primary="All Documents" />
                <Chip 
                  label={mockDocuments.length} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ borderRadius: 1 }}
                />
              </ListItem>
              {mockFolders.map((folder) => (
                <ListItem button key={folder.id}>
                  <ListItemIcon>
                    <FolderIcon />
                  </ListItemIcon>
                  <ListItemText primary={folder.name} />
                  <Chip 
                    label={folder.count} 
                    size="small" 
                    color="default" 
                    variant="outlined" 
                    sx={{ borderRadius: 1 }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Storage
            </Typography>
            <Box sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Used Space
              </Typography>
              <Typography variant="h5" fontWeight="medium">
                6.9 MB / 5 GB
              </Typography>
              <Box 
                sx={{ 
                  mt: 1,
                  height: 8, 
                  bgcolor: 'background.paper', 
                  borderRadius: 5,
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute',
                    width: '2%',
                    height: '100%',
                    bgcolor: 'primary.main'
                  }}
                />
              </Box>
            </Box>
            <Button fullWidth variant="outlined">
              Upgrade Storage
            </Button>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={9}>
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search documents..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>
          
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Recent Documents
              </Typography>
            </Box>
            <List>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => (
                  <ListItem 
                    key={doc.id}
                    divider
                    button
                    sx={{ px: 2, py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {doc.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={doc.name}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="body2" component="span" color="text.secondary">
                            {doc.size}
                          </Typography>
                          <Typography variant="body2" component="span" color="text.secondary">
                            {doc.date}
                          </Typography>
                          <Chip 
                            label={doc.folder} 
                            size="small" 
                            variant="outlined" 
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small">
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No documents found matching your search
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Documents; 